/* eslint-env browser */
import React, { useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { useSelector } from 'react-redux'
import cn from 'classnames'
import { getCartCheckoutUrl } from '../../redux/slices/cart'
import theme from '../../style/theme'
import { useHoverCursor } from '../Cursor'
import { span } from '../../style/span'
import { vw } from '../../style/vw'
import PostcodeValidator from './PostcodeValidator'

const CartFooter = ({ cartIsEmpty, isLoading }) => {
  const classes = useStyles()
  const checkoutUrl = useSelector(getCartCheckoutUrl)
  const checkoutButtonRef = useRef()
  const [isProcessing, setIsProcessing] = useState(false)
  const [checkoutError, setCheckoutError] = useState(null)
  const [postcodeValid, setPostcodeValid] = useState(false)

  useHoverCursor(checkoutButtonRef)

  const handleCheckout = () => {
    if (checkoutUrl && !cartIsEmpty && !isProcessing && !isLoading && postcodeValid) {
      setIsProcessing(true)
      setCheckoutError(null)

      // Get cart data from localStorage
      const cartId = window.localStorage.getItem('cartId')
      let finalCheckoutUrl = checkoutUrl

      try {
        if (cartId) {
          // Format URL with proper query parameter syntax
          if (!finalCheckoutUrl.includes('?')) {
            finalCheckoutUrl += `?cartId=${encodeURIComponent(cartId)}`
          } else if (!finalCheckoutUrl.includes('cartId=')) {
            finalCheckoutUrl += `&cartId=${encodeURIComponent(cartId)}`
          }

          // Add selling plan IDs if available
          const cartLines = JSON.parse(window.localStorage.getItem('cartLines') || '[]')
          if (cartLines.length > 0) {
            cartLines.forEach((line, index) => {
              if (line.sellingPlanId) {
                finalCheckoutUrl += `&spId${index}=${encodeURIComponent(line.sellingPlanId)}&lineId${index}=${encodeURIComponent(line.lineItemId)}`
              }
            })
          }
        }

        // Log the final URL for debugging
        console.log('Redirecting to checkout URL:', finalCheckoutUrl)

        // Redirect to checkout - wait a moment for visual feedback
        setTimeout(() => {
          window.location.href = finalCheckoutUrl
        }, 500)
      } catch (error) {
        console.error('Error navigating to checkout:', error)
        setCheckoutError('There was an error proceeding to checkout. Please try again.')
        setIsProcessing(false)
      }
    }
  }

  return (
    <div className={classes.footer}>
      <PostcodeValidator isValid={postcodeValid} setValid={setPostcodeValid} />
      <button
        className={cn(classes.checkoutButton, {
          disabled: cartIsEmpty || isProcessing || isLoading || !postcodeValid
        })}
        onClick={handleCheckout}
        disabled={cartIsEmpty || isProcessing || isLoading || !postcodeValid}
        ref={checkoutButtonRef}
      >
        {isProcessing ? 'Processing...' : 'Proceed to Checkout'}
      </button>

      {checkoutError && (
        <div className={classes.errorMessage}>
          {checkoutError}
        </div>
      )}

      <div className={classes.secondaryLinks}>
        <a href='/our-products' className={classes.link}>
          Continue Shopping
        </a>
      </div>
    </div>
  )
}

const useStyles = createUseStyles({
  footer: {
    paddingTop: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    [theme.breakpoints.up('md')]: {
      paddingTop: span(0, 'md')
    }
  },

  checkoutButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: [vw(12), vw(20)],
    backgroundColor: theme.colors.primary,
    color: theme.colors.white,
    border: [1, 'solid', theme.colors.primary],
    fontSize: vw(16),
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: vw(15),
    position: 'relative',
    overflow: 'hidden',

    '&:hover': {
      backgroundColor: theme.colors.white,
      color: theme.colors.primary
    },

    '&.disabled, &:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      backgroundColor: theme.colors.disabled || '#cccccc',
      color: theme.colors.white,

      '&:hover': {
        backgroundColor: theme.colors.disabled || '#cccccc',
        color: theme.colors.white
      }
    },

    [theme.breakpoints.up('md')]: {
      padding: [vw(12, 'desktop'), vw(20, 'desktop')],
      fontSize: vw(16, 'desktop'),
      marginBottom: vw(15, 'desktop')
    }
  },

  errorMessage: {
    color: theme.colors.error || '#e53935',
    fontSize: vw(12),
    marginBottom: vw(15),
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      fontSize: vw(12, 'desktop'),
      marginBottom: vw(15, 'desktop')
    }
  },

  secondaryLinks: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: vw(10),
    [theme.breakpoints.up('md')]: {
      marginTop: vw(10, 'desktop')
    }
  },

  link: {
    color: theme.colors.primary,
    textDecoration: 'underline',
    fontFamily: theme.fonts.body,
    fontSize: vw(14),
    transition: 'opacity 0.2s ease',

    '&:hover': {
      opacity: 0.7
    },

    [theme.breakpoints.up('md')]: {
      fontSize: vw(14, 'desktop')
    }
  }
}, { name: 'CartFooter' })

export default CartFooter
